import React, { Component, MouseEvent } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import DataGrid, { Paging, HeaderFilter,Search, FilterRow, Column, Export, FilterPanel, GroupPanel, Pager, SearchPanel, Format, StateStoring, Scrolling } from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import LightModePageTemplate from "../page/Templates/LightModeTemplate";
import PaymentService from "../../services/PaymentService";
import { buildRequestParameters } from "../../services/RemoteOperationsService";
import MatchIdActionCell from "./MatchIdActionCell";
import PaymentQueryActionCell from "./PaymentQueryActionCell";
import PayRequestViewActionCell from "./PayRequestViewActionCell";
import PayRequestEditActionCell from "./PayRequestEditActionCell";
import UserService, { RoleGroupNames } from "../../services/UserService";
import { paymentRequestGridTypes } from "../../services/SimpleGridService";
import sharedUtils from "../grid/sharedUtilities";
import { PaymentTypes } from "../../services/FindAnArtistService";
import { ServiceType } from "../../common/ServiceUtilities";
import SimpleShiftGlobalFilterService from "../../services/SimpleShiftGlobalFilterService";
import TagCell from "../GigRequest/TagCell";
import CookieService, { DXGridCookieKeyTypes } from "../../services/CookieService";
import { Link } from "react-router-dom";
import { confirm } from "devextreme/ui/dialog";
import { Popup, ScrollView } from "devextreme-react";
import SupplierInformationMainContent from "../SupplierInformation/SupplierInformationMainContent";
import ArtistProfileComponent from "../Artist-Profile/ArtistProfileComponent";
import { onExportingEvent } from "../../types/DevExtremeTypes";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";

let isExport: boolean = false;

// The backend does not support filtering on bankAccountVerified, so this field is filtered locally.
const BANK_ACCOUNT_VERIFIED_FIELD = "bankAccountVerified";
const BANK_ACCOUNT_VERIFIED_HEADER_FILTER_ITEMS = [
    { text: "Yes", value: true },
    { text: "No", value: false },
];

/**
 * Returns true when the DevExtreme filter node is a single condition, e.g. ["field", "=", value].
 */
const isFilterCondition = (filter: any): boolean => {
    return Array.isArray(filter) && typeof filter[0] === "string" && filter[0] !== "!";
};

/**
 * Checks whether the filter references the bank account verified field, either in every condition ("all") or in at least one ("some").
 */
const filterReferencesBankField = (filter: any, mode: "all" | "some"): boolean => {
    if (!Array.isArray(filter)) return false;
    if (isFilterCondition(filter)) return filter[0] === BANK_ACCOUNT_VERIFIED_FIELD;
    if (filter[0] === "!") return filterReferencesBankField(filter[1], mode);
    const operands = filter.filter((item: any) => Array.isArray(item));
    if (operands.length === 0) return false;
    return mode === "all"
        ? operands.every((item: any) => filterReferencesBankField(item, mode))
        : operands.some((item: any) => filterReferencesBankField(item, mode));
};

/**
 * Combines filter expressions with "and", returning null when there is nothing to combine.
 */
const combineFiltersWithAnd = (filters: any[]): any => {
    if (filters.length === 0) return null;
    if (filters.length === 1) return filters[0];
    return filters.reduce((acc: any[], item: any, index: number) => index === 0 ? [item] : [...acc, "and", item], []);
};

/**
 * Splits a grid filter into the part sent to the API and the bank account verified part applied locally.
 * Only top-level "and" conjuncts can be separated; mixed "or" groups are left untouched for the server.
 */
const splitBankAccountVerifiedFilter = (filter: any): { serverFilter: any, localFilter: any } => {
    if (!filterReferencesBankField(filter, "some")) {
        return { serverFilter: filter, localFilter: null };
    }
    if (filterReferencesBankField(filter, "all")) {
        return { serverFilter: null, localFilter: filter };
    }
    const isAndGroup = !isFilterCondition(filter) && filter[0] !== "!"
        && filter.every((item: any) => Array.isArray(item) || String(item).toLowerCase() === "and");
    if (!isAndGroup) {
        return { serverFilter: filter, localFilter: null };
    }
    const serverParts: any[] = [];
    const localParts: any[] = [];
    filter.filter((item: any) => Array.isArray(item)).forEach((item: any) => {
        const split = splitBankAccountVerifiedFilter(item);
        if (split.serverFilter) serverParts.push(split.serverFilter);
        if (split.localFilter) localParts.push(split.localFilter);
    });
    return { serverFilter: combineFiltersWithAnd(serverParts), localFilter: combineFiltersWithAnd(localParts) };
};

/**
 * Evaluates a bank-account-verified-only filter against a row. Null/undefined values are treated as "No" to match the cell display.
 */
const matchesBankAccountVerifiedFilter = (row: any, filter: any): boolean => {
    if (isFilterCondition(filter)) {
        const operation = filter.length === 2 ? "=" : filter[1];
        const expected = Boolean(filter.length === 2 ? filter[1] : filter[2]);
        const actual = Boolean(row?.[BANK_ACCOUNT_VERIFIED_FIELD]);
        if (operation === "=") return actual === expected;
        if (operation === "<>") return actual !== expected;
        return true;
    }
    if (filter[0] === "!") return !matchesBankAccountVerifiedFilter(row, filter[1]);
    let result: boolean | undefined;
    let operator = "and";
    filter.forEach((item: any) => {
        if (!Array.isArray(item)) {
            operator = String(item).toLowerCase();
            return;
        }
        const value = matchesBankAccountVerifiedFilter(row, item);
        result = result === undefined ? value : operator === "or" ? result || value : result && value;
    });
    return result ?? true;
};

/**
 * Reads a (possibly nested) field value from a row using a DevExtreme selector such as "venue.venueName".
 */
const getSelectorValue = (row: any, selector: any): any => {
    if (typeof selector === "function") return selector(row);
    return String(selector).split(".").reduce((value: any, key: string) => value?.[key], row);
};

/**
 * Builds DevExtreme remote-grouping results ({ key, items, count }) from already filtered rows.
 */
const buildLocalGroups = (rows: any[], groups: any[], level: number): any[] => {
    const { selector, desc, isExpanded } = groups[level];
    const groupedRows = new Map<any, any[]>();
    rows.forEach((row: any) => {
        const key = getSelectorValue(row, selector) ?? null;
        const bucket = groupedRows.get(key);
        bucket ? bucket.push(row) : groupedRows.set(key, [row]);
    });
    const keys = Array.from(groupedRows.keys()).sort((a: any, b: any) => {
        if (a === b) return 0;
        if (a === null) return desc ? 1 : -1;
        if (b === null) return desc ? -1 : 1;
        const compared = typeof a === "string" && typeof b === "string" ? a.localeCompare(b) : (a < b ? -1 : 1);
        return desc ? -compared : compared;
    });
    const isLastLevel = level === groups.length - 1;
    return keys.map((key: any) => {
        const groupRows = groupedRows.get(key) || [];
        let items: any[] | null = null;
        if (isExpanded) {
            items = isLastLevel ? groupRows : buildLocalGroups(groupRows, groups, level + 1);
        }
        return { key, items, count: groupRows.length };
    });
};

/**
 * Applies the given skip/take paging to an array.
 */
const applyLocalPaging = (items: any[], skip?: number, take?: number): any[] => {
    const start = skip || 0;
    return take ? items.slice(start, start + take) : items.slice(start);
};

interface PaymentRequestGridProps extends WithTranslation {
    serviceTypeId: string,
    startDate: string,
    endDate: string,
    venueId: string,
    providerId: string,
    navigateFrom?: string,
    setPaymentGridRecordDataSource: (count: number, dataSource: any[]) => void;
    getBuild: (flag: boolean) => void;  
    resetRefreshGrid?: (flag: boolean) => void;
    gridType: string;
    isHide: boolean;
    providerPayAmount: number | undefined;
    paymentRequestId: string;
    paymentTypeId: string;
    location: any;
    history: any;
    paymentRequestType: string;
    isRefreshButtonClicked?: boolean;     
}

interface PaymentRequestGridState {
    dataSource: [];
    totalCount: number;
    showButton: boolean;
    expanded: boolean;
    expandedText: string;
    collapsedText: string;
    buttonText: string;
    parameterVal: string;
    promiseResponse: any;
    loadIndicatorForGrid: boolean;
    providerPopupVisibleEntertainment?: boolean;
    providerPopupVisibleOther?: boolean;
    clientId: string;
    venueId: string;
    serviceTypeId: string;
    businessEntityId: string;
}

class PaymentRequestGrid extends Component<PaymentRequestGridProps> {
    state: PaymentRequestGridState;
    paymentService: PaymentService;
    sharedUtils: sharedUtils;
    remoteOperations: {
        sorting: boolean,
        paging: boolean,
        filtering: boolean,
        grouping: boolean,
        summary: boolean
    }
    isEditMode: string;
    dataGridRef: any;
    clearRef:any;
    // Rows fetched without the bank account verified filter; reused so changing that filter does not call the API.
    bankFilterRowsCache: { params: string, rows: any[] } | null = null;
    constructor(props: PaymentRequestGridProps) {
        super(props);
        this.isEditMode = "";
        this.state = {
            dataSource: [],
            totalCount: 0,
            showButton: false,
            expanded: false,
            expandedText: "Close",
            collapsedText: "View more",
            buttonText: "",
            parameterVal: "",
            promiseResponse: {},
            loadIndicatorForGrid: false,
            providerPopupVisibleEntertainment: false,
            providerPopupVisibleOther: false,
            clientId: "",
            venueId: "",
            serviceTypeId: "",
            businessEntityId: "",
        }
        this.paymentService = new PaymentService();
        this.sharedUtils = new sharedUtils();
        this.remoteOperations = {
            sorting: true,
            paging: true,
            filtering: true,
            grouping: true,
            summary: false
        }
        //this.dataGridRef = React.createRef();
        //this.dataGridRef =React.useRef<DataGrid>(null);
        this.dataGridRef = null;
        this.clearRef = React.createRef;
        this.clearRef.current = false;

    }

    clearFiltersOnLoad_DrillDown = () =>{
        //this.clearRef.current = true;
        let navigateFrom = this.props.location.state && this.props.location.state.navigateFrom || '';
        if(navigateFrom && this.clearRef?.current){
            //if(navigateFrom == "AddJobPending" || navigateFrom == "AddJobQueried" || navigateFrom=='PaymentRequestQueried'){
                this.dataGridRef?.instance()?.clearFilter();
                this.clearRef.current = false;
            //}
        }
    }

    /**
     * Grid load handler. Bank account verified filtering is not supported by the API, so when it is active
     * the remaining criteria are fetched once (unpaged) and the bank filter, paging and grouping are applied locally.
     */
    getResults = (loadOptions: any) => {
        const { serverFilter, localFilter } = splitBankAccountVerifiedFilter(loadOptions.filter);
        const groups = Array.isArray(loadOptions.group)
            ? loadOptions.group.map((group: any) => typeof group === "string" ? { selector: group } : group)
            : [];
        const hasGroupInterval = groups.some((group: any) => group.groupInterval);

        if (!localFilter) {
            return this.fetchGridRows(this.buildResultParams(loadOptions));
        }
        // Date-interval groups (header filters on date columns) cannot be reproduced locally, so use the API without the bank filter.
        if (hasGroupInterval) {
            return this.fetchGridRows(this.buildResultParams({ ...loadOptions, filter: serverFilter }));
        }

        const params = this.buildResultParams({
            ...loadOptions,
            filter: serverFilter,
            skip: undefined,
            take: undefined,
            group: undefined,
            requireGroupCount: undefined,
            totalSummary: undefined,
            groupSummary: undefined,
        });
        const rowsPromise = this.bankFilterRowsCache && this.bankFilterRowsCache.params === params
            ? Promise.resolve(this.bankFilterRowsCache.rows)
            : this.fetchGridRows(params).then((result: any) => {
                this.bankFilterRowsCache = { params, rows: result.data };
                return result.data;
            });

        return rowsPromise.then((rows: any[]) => {
            const filteredRows = rows.filter((row: any) => matchesBankAccountVerifiedFilter(row, localFilter));
            if (groups.length > 0) {
                const groupedRows = buildLocalGroups(filteredRows, groups, 0);
                return {
                    data: applyLocalPaging(groupedRows, loadOptions.skip, loadOptions.take),
                    totalCount: filteredRows.length,
                    groupCount: groupedRows.length,
                };
            }
            return {
                data: applyLocalPaging(filteredRows, loadOptions.skip, loadOptions.take),
                totalCount: filteredRows.length,
            };
        });
    }

    /**
     * Calls the payment request grid API with the given query string.
     */
    fetchGridRows = (params: string) => {
        return this.paymentService.getPaymentGridRowsWithinRemoteOperations(params)
            .then(this.handleSuccessLoadDataSource)
            .catch(() => { throw 'Data Loading Error'; });
    }

    buildResultParams = (loadOptions: any): string => {
        this.clearFiltersOnLoad_DrillDown()
        let params = "";
        let paymentTypeId: string = "";
        if (this.props.paymentTypeId && this.props.paymentTypeId != "0") {
            paymentTypeId = this.props.paymentTypeId;
        }

        if (this.props.location.state && this.props.location.state.navigateFrom == "IncompletePaymentRequest") {
            const serviceTypeId = this.props.location.state.serviceType;
            const serviceTypeFilter = serviceTypeId == ServiceType.Entertainment
                ? [["serviceType.id", "=", serviceTypeId], "or", ["serviceType.id", "=", null]]
                : ["serviceType.id", "=", serviceTypeId];
            const filters = [
                [["paymentRequestStatusLookUp.value", "=", "Provider Draft Incomplete"], "or", ["paymentRequestStatusLookUp.value", "=", "Provider Job Incomplete"]],
                "and",
                serviceTypeFilter
            ];
            params = buildRequestParameters(loadOptions, 'filter', filters);
            params += `&dateRangeFrom=&dateRangeTo=&serviceTypeId=${serviceTypeId}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
        }
        else if (this.props.location.state && this.props.location.state.navigateFrom == "PaymentRequestPending") {
            let filters = [["paymentRequestStatusLookUp.value","=","Not Matched"],"or",["paymentRequestStatusLookUp.value","=","Draft"],"or",["paymentRequestStatusLookUp.value","=","Pending"]];
            params = buildRequestParameters(loadOptions,'filter',filters);
            params += `&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;

            //if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&filter=[["paymentRequestStatusLookUp.value","=","Not Matched"],"or",["paymentRequestStatusLookUp.value","=","Draft"],"or",["paymentRequestStatusLookUp.value","=","Pending"]]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
            //else params = buildRequestParameters(loadOptions) + `?filter=[["paymentRequestStatusLookUp.value","=","Not Matched"],"or",["paymentRequestStatusLookUp.value","=","Draft"],"or",["paymentRequestStatusLookUp.value","=","Pending"]]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
        }
        else if (this.props.location.state && this.props.location.state.navigateFrom == "AddJobPending") {
            let filters = [["paymentRequestStatusLookUp.value","=","Add Job Draft"],"or",["paymentRequestStatusLookUp.value","=","Add Job Pending"]];
            params = buildRequestParameters(loadOptions,'filter',filters);
            params += `&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
           // if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&filter=[["paymentRequestStatusLookUp.value","=","Add Job Draft"],"or",["paymentRequestStatusLookUp.value","=","Add Job Pending"]]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
            //else params = buildRequestParameters(loadOptions) + `?filter=[["paymentRequestStatusLookUp.value","=","Add Job Draft"],"or",["paymentRequestStatusLookUp.value","=","Add Job Pending"]]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
        }
        else if (this.props.location.state && this.props.location.state.navigateFrom == "PaymentRequestQueried") {
            let filters = ["paymentRequestStatusLookUp.value","=","Queried"]
            params = buildRequestParameters(loadOptions,'filter',filters);
            params += `&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`
            
            //if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&filter=["paymentRequestStatusLookUp.value","=","Queried"]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
            //else params = buildRequestParameters(loadOptions) + `?filter=["paymentRequestStatusLookUp.value","=","Queried"]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
        }
        else if (this.props.location.state && this.props.location.state.navigateFrom == "AddJobQueried") {
            let filters = ["paymentRequestStatusLookUp.value","=","Add Job Queried"];
            params = buildRequestParameters(loadOptions,'filter',filters);
            params += `&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`
            //if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&filter=["paymentRequestStatusLookUp.value","=","Add Job Queried"]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
            //else params = buildRequestParameters(loadOptions) + `?filter=["paymentRequestStatusLookUp.value","=","Add Job Queried"]&dateRangeFrom=&dateRangeTo=&serviceTypeId=${this.props.location.state.serviceType}&unionVenueAndProviders=false&venueId=&providerId=&typeId=`;
        }
        else if (this.props.navigateFrom == "matchedPopUp" && this.props.paymentRequestId) {
            if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&dateRangeFrom=${this.props.startDate}&dateRangeTo=${this.props.endDate}&serviceTypeId=${this.props.serviceTypeId}&unionVenueAndProviders=${"true"}&venueId=${this.props.venueId}&providerId=${this.props.providerId}&hiddenPaymentRequestId=${this.props.paymentRequestId}&filterBy=${this.props.paymentTypeId}`;
            else params = buildRequestParameters(loadOptions) + `?dateRangeFrom=${this.props.startDate}&dateRangeTo=${this.props.endDate}&serviceTypeId=${this.props.serviceTypeId}&unionVenueAndProviders=${"true"}&venueId=${this.props.venueId}&providerId=${this.props.providerId}&hiddenPaymentRequestId=${this.props.paymentRequestId}&filterBy=${this.props.paymentTypeId}`;
        }
        else if (this.props.navigateFrom != "matchedPopUp") {
            if(buildRequestParameters(loadOptions) !== "") params = buildRequestParameters(loadOptions) + `&dateRangeFrom=${this.props.startDate}&dateRangeTo=${this.props.endDate}&serviceTypeId=${this.props.serviceTypeId}&unionVenueAndProviders=${"false"}&venueId=${this.props.venueId}&providerId=${this.props.providerId}&typeId=${paymentTypeId}`;
            else params = buildRequestParameters(loadOptions) + `?dateRangeFrom=${this.props.startDate}&dateRangeTo=${this.props.endDate}&serviceTypeId=${this.props.serviceTypeId}&unionVenueAndProviders=${"false"}&venueId=${this.props.venueId}&providerId=${this.props.providerId}&typeId=${paymentTypeId}`;
        }
        else if (isExport) {            
            params = `?dateRangeFrom=${this.props.startDate}&dateRangeTo=${this.props.endDate}&serviceTypeId=${this.props.serviceTypeId}&unionVenueAndProviders=${"false"}&venueId=${this.props.venueId}&providerId=${this.props.providerId}&typeId=${paymentTypeId}`;
        }
        return params;
    }

    loadDataSource = () => {
        if(this.props.isRefreshButtonClicked == true) {
            this.props.resetRefreshGrid ? this.props.resetRefreshGrid(false) : null;
        }
        this.bankFilterRowsCache = null;
        let dataSource = new CustomStore({
            key: "id",
            load: this.getResults,
        });

        this.setState({
            dataSource: dataSource
        });
    }

    handleSuccessLoadDataSource = (response: any) => {
        this.setState({
            loadIndicatorForGrid: false
        });
        // show notification for new build            
        let version = localStorage.getItem("BuildVersion");
        let headerVersion = response.headers["x-application-version"];
        // if(version == "" || version == null){
            //     version = getHeaderVersion;
            //     localStorage.setItem("BuildVersion", getHeaderVersion);
            // }        
            if(version !== headerVersion){
                localStorage.setItem("BuildVersion", headerVersion);
                this.props.getBuild(true);              
            }
        // end
        return {
            data: response.data.data ? response.data.data : [],
            totalCount: response.data.totalCount ? response.data.totalCount : 0
        };
    }

    componentDidMount() {
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp) {
            this.loadDataSource();
        }
        else {
            this.setState({
                loadIndicatorForGrid: true
            });
            SimpleShiftGlobalFilterService.removeDateFromvalue();
            SimpleShiftGlobalFilterService.removeDateToValue();
        }

        if(this.props?.location?.state?.pointOfNavigation == 'home')
        {
            this.clearRef.current = true;
        }
    }
    componentDidUpdate(prevProps: PaymentRequestGridProps) {
        if (prevProps.venueId != this.props.venueId || prevProps.providerId != this.props.providerId || prevProps.serviceTypeId != this.props.serviceTypeId || prevProps.startDate != this.props.startDate 
            || prevProps.endDate != this.props.endDate || prevProps.paymentTypeId != this.props.paymentTypeId || (this.props.isRefreshButtonClicked != prevProps.isRefreshButtonClicked && this.props.isRefreshButtonClicked == true)) {
            this.loadDataSource();
        }
    }

    venueCellDisplayContent = (cellInfo: any) => {
        var isShowInColour: boolean = false;
        var x = this.props.venueId;
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp && (cellInfo.data.venue?.id.toString() == this.props.venueId)) {
            {
                isShowInColour = true;
            }
        }
        return (
            <div className="container-fluid">
                <div className="row">
                    {isShowInColour ? <span className="grid-info__text_color_green">{cellInfo.data.venue?.venueName}</span> : cellInfo.data.venue?.venueName}
                </div>
            </div>
        );
    };

    typeCellDisplayContent = (cellInfo: any) => {
        var type: string = "";
        if (cellInfo && cellInfo.data.paymentRequestType && cellInfo.data.paymentRequestType.id == PaymentTypes.PaymentRequest) {
            type = "Payment";
        }
        else if (cellInfo && cellInfo.data.paymentRequestType && cellInfo.data.paymentRequestType.id == PaymentTypes.AddJob) {
            type = "Job";
        }

        return (
            <div className="container-fluid">
                <span>{type}</span>
            </div>
        );
    }

    showInfoProvider = (clientId: string, venueId: string, serviceTypeId: string, businessEntityId: string) => {
        this.setState({
            clientId: clientId,
            venueId: venueId,
            serviceTypeId: serviceTypeId,
            businessEntityId: businessEntityId,

        });
        if (serviceTypeId == "30") {
            this.setState({
                providerPopupVisibleOther: false,
                providerPopupVisibleEntertainment: true,
            });
        }
        else {
            this.setState({
                providerPopupVisibleEntertainment: false,
                providerPopupVisibleOther: true,
            });
        }
    };   

    providerCellDisplayContent = (cellInfo: any) => {
        var provider: string = cellInfo.data.providerBusinessEntity.shortName;
        var isShowInColour: boolean = false;
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp && (cellInfo.data.providerBusinessEntity.id.toString() == this.props.providerId)) {
            isShowInColour = true;
        }
        return (
            <div>
                <Link
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        this.showInfoProvider(cellInfo.data.clientBusinessEntity.id, cellInfo.data.venue.id, cellInfo.data.serviceType.id, cellInfo.data.providerBusinessEntity.id);
                    }}
                    className="approval-query-column"
                    to="#"
                >
                    {isShowInColour ? <span className="grid-info__text_color_green">{provider}</span> : provider}
                </Link>
            </div>
            
        );
        
    };

    hideInfoProviderArtistProfile = (e: any) => {
        e.cancel = true;
        if (this.isEditMode == "editMode") {
            confirm("<div class='text-center'>Close screen without saving?</div>", "Close").then((hasConfirmed) => {
                if (hasConfirmed) {
                    this.setState({
                        providerPopupVisibleEntertainment: false,
                    });
                    this.isEditMode = "";
                }
            });
        }
        else {
            this.setState({
                providerPopupVisibleEntertainment: false,
            });
        }
    };

    handlePopUpClose = (flag: boolean, editmode: string) => {
        this.isEditMode = editmode;
        this.setState({
            providerPopupVisibleEntertainment: flag,
        });
    }
    hideInfoProvider = (e: any) => {
        this.setState({
            providerPopupVisibleOther: false,
        });
    };

onExporting = (e: onExportingEvent) => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Main sheet");

  exportDataGrid({
    component: e.component,
    worksheet: worksheet,
    autoFilterEnabled: true,
    customizeCell: ({ gridCell, excelCell }) => {
      if (!gridCell || !gridCell.column) return;

      const field = gridCell.column.dataField;

      // Update header text
      if (gridCell.rowType === "header" && field === "providerBusinessEntity.professionalName") {
        excelCell.value = "ACT NAME";
        return;
      }

      if (gridCell.rowType !== "data") return;

      if (field === "providerBusinessEntity.professionalName") {
        const data = gridCell.data;

        if (this.props.serviceTypeId === "30" && data?.providerBusinessEntityProfileId) {
          excelCell.value =
            `${data.providerBusinessEntityProfile?.professionalName ?? ""} (${data.providerBusinessEntityProfile?.profileId ?? ""})`;
        } else {
          excelCell.value = data?.providerBusinessEntity?.professionalName ?? "";
        }
      }
    }
  }).then(() => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), "DataGrid.xlsx");
    });
  });

  e.cancel = true;
};




    displayBooleanContent = (Val:any)=>{
        let valToShow: string = "";
        if (Val) {
            valToShow = "Yes";
        }
        else {
            valToShow = "No";
        }
        return (
            <div className="container-fluid">
                <span>{valToShow}</span>
            </div>
        );
    }

    dateCellDisplayContent = (cellInfo: any) => {
        let date: string = cellInfo.text;
        let isShowInColour: boolean = false;
        if ((this.props.gridType == paymentRequestGridTypes.matchedPopUp) && (cellInfo.text && cellInfo.text == this.props.startDate)) {
            isShowInColour = true;
        }
        return (
            <div className="container-fluid">
                {isShowInColour ? <span className="grid-info__text_color_green">{date}</span> : date}
            </div>
        );
    };
    displayActName = (cellInfo: any)=>{
        if(this.props.serviceTypeId === "30"){
            if(cellInfo.data.providerBusinessEntityProfileId){
                return (
            <div className="container-fluid">
                <span>{`${cellInfo.data.providerBusinessEntityProfile.professionalName} (${cellInfo.data.providerBusinessEntityProfile.profileId})`}</span>
            </div>
                )
            }else{
                return(
                <div className="container-fluid">
                <span>{cellInfo.data.providerBusinessEntity.professionalName}</span>
            </div>
            )
            }
        }else{
            return(
                <div className="container-fluid">
                <span>{cellInfo.data.providerBusinessEntity.professionalName}</span>
            </div>
            )
        }
    }
    providerNetCellDisplayContent = (cellInfo: any) => {
        var providerAmount: number = cellInfo.data.providerPayAmountLocal;
        var providerPayAmount: string = providerAmount.toString().slice(1);
        var providerAmountVal: number = parseInt(providerPayAmount);
        var isShowInColour: boolean = false;
        if ((this.props.gridType == paymentRequestGridTypes.matchedPopUp) && (providerAmountVal && providerAmountVal == this.props.providerPayAmount)) {
            isShowInColour = true;
        }
        return (
            <div className="container-fluid">
                {isShowInColour ? <span className="grid-info__text_color_green">{this.sharedUtils.thousandsSeparator(providerAmount.toString())}</span> : this.sharedUtils.thousandsSeparator(providerAmount.toString())}
            </div>
        );
    };
    fasterPaymentCellDisplayContent = (cellInfo: any) => {
        let fasterpayVal = cellInfo.data.fasterPayment;
        let isShowInColour: boolean = false;
        let valToShow: string = "";
        if (fasterpayVal) {
            isShowInColour = true;
            valToShow = "Yes";
        }
        else {
            valToShow = "No";
        }
        return (
            <div className="container-fluid">
                {isShowInColour ? <span className="grid-info__text_color_green">{valToShow}</span> : valToShow}
            </div>
        );

    }

    onboardedCellDisplayContent = (cellInfo: any) => {
        let onboardedVal = cellInfo.data.venue?.onboarded;
        let valToShow: string = "";
        if (onboardedVal) {
            valToShow = "Yes";
        }
        else {
            valToShow = "No";
        }
        return (
            <div className="container-fluid">
                {valToShow}
            </div>
        );

    }

    onContentReady = (e: any) => {
        var totalCount: number = 0;
        var items: any[] = [];
        if (e.component.getDataSource().totalCount()) {
            totalCount = e.component.getDataSource().totalCount();
            items = e.component.getDataSource().items();
        }
        this.props.setPaymentGridRecordDataSource(totalCount, items);
    }

    setCollapsibleSection = (totalCount: number) => {
        if (totalCount > 0) {
            this.setState({
                showButton: true,
                expanded: false,
                buttonText: this.state.collapsedText
            });
        }
    }

    renderRequestDateCellDisplayContent = (cellInfo: any) => {
        var requestDate: string = cellInfo.data.requestDate.toString();
        var requestDateParts: string[] = requestDate.split(" ");
        var part1 = requestDateParts[0];
        var part2 = requestDateParts[1];
        var valueToShow: string = part1 + " " + part2;
        var isShowInColour: boolean = false;
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp) {
            isShowInColour = true;
        }
        return (
            <div className="container-fluid">
                {<span>{valueToShow}</span>}
            </div>
        );
    }

    onCustomGridLoad = () => {
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp) {
            return CookieService.loadDXGridConfiguration(DXGridCookieKeyTypes.paymentRequestGridMatched);
        }
        else {
            return CookieService.loadDXGridConfiguration(DXGridCookieKeyTypes.paymentRequestGridForm);
        }
    }

    onCustomGridSave = (gridState: Object) => {
        if (this.props.gridType == paymentRequestGridTypes.matchedPopUp) {
            CookieService.saveDXGridConfiguration(DXGridCookieKeyTypes.paymentRequestGridMatched, gridState);
        }
        else {
            CookieService.saveDXGridConfiguration(DXGridCookieKeyTypes.paymentRequestGridForm, gridState);
        }
    }

    renderPaymentGrid = () => {
        var isProvider = UserService.isUserInGroup(RoleGroupNames.ProviderScheduler);
        return (

            <DataGrid
                elementAttr={{
                    id: "gridContainer"
                }}
                ref={(ref) => { this.dataGridRef = ref; }}
                dataSource={this.state.dataSource}
                showBorders={false}
                showColumnLines={false}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                columnResizingMode={"widget"}
                hoverStateEnabled={true}
                wordWrapEnabled={true}
                remoteOperations={this.remoteOperations}
                noDataText={"No data found for the filter applied - please correct or expand the filters used"}
                onContentReady={this.onContentReady}
                onExporting={this.onExporting}
            >
                <Paging defaultPageSize={this.props.navigateFrom == "matchedPopUp" ? 5 : 10} />
                <Scrolling useNative={true} showScrollbar={"always"} />
                <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                <HeaderFilter visible={true}>
                        <Search enabled={true}/> 
                </HeaderFilter>
                <Export enabled={true} />
                <FilterRow visible={true} applyFilter="auto" />
                <GroupPanel visible={true} />
                <FilterPanel visible={true} />
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={this.onCustomGridLoad}
                    customSave={this.onCustomGridSave}
                />
                <SearchPanel visible={true} placeholder={"Search"} />
                <Column
                    width="120"
                    caption="MATCHED"
                    cellComponent={MatchIdActionCell}
                    allowExporting={false}
                />
                <Column
                    width="120"
                    caption="QUERY"
                    cellComponent={PaymentQueryActionCell}
                    allowExporting={false}
                />
                {isProvider ? null :
                    <Column
                        width="120"
                        caption="CHANGE"
                        cellComponent={PayRequestEditActionCell}
                        allowExporting={false}
                    />}
                <Column
                    width="120"
                    caption="VIEW"
                    cellComponent={PayRequestViewActionCell}
                    allowExporting={false}
                />
                <Column
                    caption="TAG ME"
                    cellComponent={TagCell}

                ></Column>
                <Column
                    dataField="taggedUser.name"
                    caption="TAGGED"
                    width="120"
                />
                <Column
                    dataField="id"
                    caption="ID"
                />
                <Column
                    dataField="paymentRequestType.value"
                    caption="TYPE"
                    cellRender={this.typeCellDisplayContent}
                />
                <Column
                    dataField="paymentRequestStatusLookUp.value"
                    caption="STATUS"
                />
                <Column
                    dataField="providerBusinessEntity.shortName"
                    caption="PROVIDER/ARTIST"
                    cellRender={this.providerCellDisplayContent}
                />
                <Column
                    dataField="providerBusinessEntity.id"
                    caption="PROVIDER ID"
                />
                <Column
                    dataField="providerBusinessEntity.professionalName"
                    cellRender={this.displayActName}
                    caption="ACT NAME"
                />
                <Column
                        dataField="providerBusinessEntity.verified"
                        caption="BUSINESS SET-UP VERIFIED"
                        // cellRender={this.feedbackCellDisplayContent}
                        cellComponent={(cellInfo: any) => { return this.displayBooleanContent(cellInfo?.data?.data?.providerBusinessEntity?.verified) }}
                        dataType="boolean"
                    />
                    <Column
                        dataField="providerBusinessEntity.vatVerified"
                        caption="VAT VERIFIED"
                        cellComponent={(cellInfo: any) => { return this.displayBooleanContent(cellInfo?.data?.data?.providerBusinessEntity?.vatVerified) }}
                        dataType="boolean"
                    //cellRender={this.feedbackCellDisplayContent}
                    />
                    <Column
                        dataField="bankAccountVerified"
                        caption="BANK ACCOUNT VERIFIED"
                        cellComponent={(cellInfo: any) => { return this.displayBooleanContent(cellInfo?.data?.data?.bankAccountVerified) }}
                        // Filtered locally in getResults (not supported by the API); static header filter avoids a server group request.
                        dataType="boolean"
                        trueText="Yes"
                        falseText="No"
                        headerFilter={{ dataSource: BANK_ACCOUNT_VERIFIED_HEADER_FILTER_ITEMS }}
                        allowSearch={false}
                        allowSorting={false}
                    />
                <Column
                    caption="DATE FROM"
                    dataField="dateFrom"
                    cellRender={this.dateCellDisplayContent}
                    format="dd/MM/yyyy"
                    dataType="date"
                />
                <Column
                    caption="DATE TO"
                    dataField="dateTo"
                    cellRender={this.dateCellDisplayContent}
                    format="dd/MM/yyyy"
                    dataType="date"
                />
                <Column
                    caption="PROVIDER NET"
                    dataField="providerPayAmountLocal"
                    cellRender={this.providerNetCellDisplayContent}
                ><Format type="currency" currency='GBP' precision={2}></Format>
                </Column>
                <Column
                    dataField="providerPayAmountLocalExtras"
                    caption="EXTRAS"
                    dataType="number"
                >
                    <Format type="currency" currency='GBP' precision={2}></Format>
                </Column>
                <Column
                    caption="FASTER PAY"
                    dataField="fasterPayment"
                    cellRender={this.fasterPaymentCellDisplayContent}
                />
                <Column
                    dataField="clientBusinessEntity.shortName"
                    caption="CLIENT"
                />
                <Column
                    dataField="venue.venueName"
                    caption="VENUE"
                    cellRender={this.venueCellDisplayContent}
                />
                <Column
                    dataField="venue.houseNumber"
                    caption="VENUE NO"
                />
                <Column 
                    dataField="venue.onboarded" 
                    caption="ONBOARDED" 
                    cellRender={this.onboardedCellDisplayContent}
                />
                <Column
                    dataField="serviceType.value"
                    caption="SERVICE"
                />
                <Column
                    dataField="requestDate"
                    caption="REQUEST DATE"
                    format="dd/MM/yyyy"
                    dataType="date"
                // cellRender={this.renderRequestDateCellDisplayContent}
                />
                <Column
                    dataField="description"
                    caption="DESCRIPTION"
                />
                <Column
                    dataField="comments"
                    caption="COMMENTS"
                />
                <Column
                    dataField="internalComments"
                    caption="INTERNAL COMMENTS"
                />
            </DataGrid>
        );
    }


    render() {
        return (
            <>
                {this.props.navigateFrom == "matchedPopUp" ?
                    <>
                        <section className="card card-form my-5" hidden={this.props.isHide}>
                            <div className="card-body">
                                {this.renderPaymentGrid()}
                            </div></section>
                    </>

                    :
                    <LightModePageTemplate>
                        {this.renderPaymentGrid()}
                    </LightModePageTemplate>
                }
                <Popup
                    visible={this.state.providerPopupVisibleOther}
                    onHiding={this.hideInfoProvider}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    showCloseButton={true}
                    title="Supplier Information & Venue Conditions"
                    resizeEnabled={true}
                >
                    <ScrollView width="100%" height="100%">
                        <SupplierInformationMainContent
                            clientId={this.state.clientId}
                            venueId={this.state.venueId}
                            serviceTypeId={this.state.serviceTypeId}
                            navigationFromShift="SimpleShiftGrid"
                        ></SupplierInformationMainContent>
                    </ScrollView>
                </Popup>
                {
                    this.state.providerPopupVisibleEntertainment ? (
                        <Popup
                            visible={this.state.providerPopupVisibleEntertainment}
                            onHiding={this.hideInfoProviderArtistProfile}
                            dragEnabled={false}
                            hideOnOutsideClick={true}
                            showTitle={true}
                            showCloseButton={true}
                            title="Artist Details"
                            resizeEnabled={true}
                        >
                            <ScrollView width="100%" height="100%">
                                <ArtistProfileComponent
                                    mode="view"
                                    id={this.state.businessEntityId}
                                    handlePopUpClose={this.handlePopUpClose}
                                    businessEntityId={this.state.businessEntityId}
                                    handleFeedbackUpdate={() => { return null }}
                                    handleAddArtist={() => { return null }}
                                    serviceType={"Entertainment"}
                                ></ArtistProfileComponent>
                            </ScrollView>
                        </Popup>
                    ) : (null)
                }
            </>
        );
    }
}

export default withTranslation()(PaymentRequestGrid);